import { ProfileResource, getReferenceString } from '@medplum/core';
import { 
  Attachment, 
  Communication, 
  Patient, 
  Practitioner, 
  Reference 
} from '@medplum/fhirtypes';

/**
 * Represents a single message in a conversation
 */
export class ConversationMessage {
  readonly originalCommunication: Communication;

  constructor({ originalCommunication }: { originalCommunication: Communication }) {
    this.originalCommunication = originalCommunication;
  }

  static fromCommunication({ comm }: { comm: Communication }): ConversationMessage {
    return new ConversationMessage({
      originalCommunication: comm
    });
  }

  get id(): string {
    return this.originalCommunication.id!;
  }

  get text(): string {
    return this.originalCommunication.payload?.[0]?.contentString || '';
  }
  
  // Helper method to detect if this is a placeholder message for transcription
  get isTranscriptionPlaceholder(): boolean {
    const content = this.originalCommunication.payload?.[0]?.contentString || '';
    return content === '[Audio message - Transcribing...]';
  }
  
  get role(): 'user' | 'assistant' {
    // In the client portal: 
    // - Messages from practitioners or bots are from the assistant
    // - Messages from the client are from the user
    return this.originalCommunication.sender?.reference?.includes('Patient/') 
      ? 'user' 
      : 'assistant';
  }

  get sentAt(): Date {
    return new Date(this.originalCommunication.sent!);
  }

  get messageOrder(): number {
    return new Date(
      this.originalCommunication.sent || 
      this.originalCommunication.meta?.lastUpdated || 
      new Date()
    ).getTime();
  }

  get received(): Date | undefined {
    return this.originalCommunication.received
      ? new Date(this.originalCommunication.received)
      : undefined;
  }

  get read(): boolean {
    return this.originalCommunication.status === 'completed';
  }

  // Specific to ReflectionGuide: Get audio data from extensions
  get audioData(): string | undefined {
    const audioExt = this.originalCommunication.extension?.find(e => 
      e.url === 'https://progressnotes.app/fhir/StructureDefinition/reflection-guide-audio-data'
    );
    return audioExt?.valueString;
  }

  get audioContentType(): string | undefined {
    const typeExt = this.originalCommunication.extension?.find(e => 
      e.url === 'https://progressnotes.app/fhir/StructureDefinition/reflection-guide-audio-content-type'
    );
    return typeExt?.valueString || 'audio/wav';
  }

  // Legacy support for Binary references
  get audioBinaryId(): string | undefined {
    const audioExt = this.originalCommunication.extension?.find(e => 
      e.url === 'https://progressnotes.app/fhir/StructureDefinition/reflection-guide-audio'
    );
    return audioExt?.valueReference?.reference?.replace('Binary/', '');
  }

  // Check if audio is available on-demand (lazy TTS)
  get hasAudioOnDemand(): boolean {
    const audioOnDemandExt = this.originalCommunication.extension?.find(e => 
      e.url === 'https://progressnotes.app/fhir/StructureDefinition/audio-available-on-demand'
    );
    return audioOnDemandExt?.valueBoolean === true;
  }

  // Check if this message can have audio (either has it or can generate it)
  get canHaveAudio(): boolean {
    return !!this.audioData || this.hasAudioOnDemand;
  }
}

/**
 * Represents a conversation thread containing multiple messages
 */
export class ConversationThread {
  readonly messages: ConversationMessage[];
  readonly originalCommunication: Communication;

  constructor({
    messages,
    originalCommunication,
  }: {
    messages: ConversationMessage[];
    originalCommunication: Communication;
  }) {
    // Sort messages by sent time, then by ID for stability
    this.messages = [...messages].sort((a, b) => {
      // Primary sort by sent time
      const timeA = a.messageOrder || 0;
      const timeB = b.messageOrder || 0;
      if (timeA !== timeB) {
        return timeA - timeB;
      }
      
      // Secondary sort by ID for messages sent at the same time
      // This ensures consistent ordering regardless of creation order
      return (a.id || '').localeCompare(b.id || '');
    });
    
    this.originalCommunication = originalCommunication;
  }

  static fromCommunication({
    comm,
    threadMessageComms,
  }: {
    comm: Communication;
    threadMessageComms: Communication[];
  }): ConversationThread {
    return new ConversationThread({
      messages: threadMessageComms.map((comm) => ConversationMessage.fromCommunication({ comm })),
      originalCommunication: comm,
    });
  }

  get id(): string {
    return this.originalCommunication.id!;
  }

  get sessionId(): string | undefined {
    const sessionIdExt = this.originalCommunication.extension?.find(ext => 
      ext.url === 'https://progressnotes.app/fhir/StructureDefinition/session-id'
    );
    return sessionIdExt?.valueString;
  }

  get topic(): string {
    return this.originalCommunication.payload?.[0]?.contentString || 'Reflection Guide Session';
  }

  get lastMessage(): string | undefined {
    const lastMsg = this.messages[this.messages.length - 1];
    return lastMsg?.text;
  }

  get lastMessageSentAt(): Date | undefined {
    const lastMsg = this.messages[this.messages.length - 1];
    return lastMsg?.sentAt;
  }

  get threadOrder(): number {
    return new Date(
      this.lastMessageSentAt ||
        this.originalCommunication.sent ||
        this.originalCommunication.meta?.lastUpdated ||
        new Date(),
    ).getTime();
  }

  get clientId(): string | undefined {
    // Note: Still using Patient resource internally
    const clientRef = this.originalCommunication.subject?.reference;
    return clientRef?.replace('Patient/', '');
  }

  get isReflectionThread(): boolean {
    return (
      this.originalCommunication.extension?.some(
        (e) => e.url === "https://progressnotes.app/fhir/StructureDefinition/reflection-thread",
      ) ?? false
    );
  }

  get isClientInitiated(): boolean {
    const selfInitiatedExt = this.originalCommunication.extension?.find(ext => 
      ext.url === 'https://progressnotes.app/fhir/StructureDefinition/self-initiated'
    );
    return selfInitiatedExt?.valueBoolean === true;
  }
  
  // Keep the old property name for backwards compatibility
  get isPatientInitiated(): boolean {
    return this.isClientInitiated;
  }

  /**
   * Get the conversation history in the format expected by Claude API
   */
  getClaudeMessages(): { role: string; content: string }[] {
    return this.messages.map(msg => ({
      role: msg.role === 'user' ? 'user' : 'assistant',
      content: msg.text
    }));
  }
}